"use client";

import { useState, type ComponentType } from "react";
import Image from "next/image";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Tabs } from "radix-ui";
import { AppWindow, ArrowRight, ArrowUpRight, Globe, Instagram, Linkedin, Palette, Smartphone, Workflow } from "lucide-react";
import { BookCall } from "@/components/booking/BookCall";
import { BOOKING_INTRO } from "@/lib/booking";
import { buttonVariants } from "@/components/ui/button";
import { SocialFeed } from "@/components/work/SocialFeed";
import { socialKind } from "@/lib/social";
import { cn } from "@/lib/utils";
import { displayHost, objectPosition, type Client, type Deliverable, type DeliverableKind, type Shot, type Story } from "@/lib/work";

const EASE = [0.16, 1, 0.3, 1] as const;

export const DELIVERABLE_ICON: Record<DeliverableKind, ComponentType<{ className?: string }>> = {
  website: Globe,
  app: Smartphone,
  erp: Workflow,
  build: AppWindow,
  content: Palette,
  instagram: Instagram,
  linkedin: Linkedin,
};

/** The small print under a tab: the host, how many pieces, or "full feed". */
const detailFor = (d: Deliverable): string => {
  if (d.kind === "website") return (d.url && displayHost(d.url)) || "Website";
  if (d.kind === "erp") return "What it solved";
  if (d.kind === "app") return "iOS and Android";
  if (d.kind === "instagram" || d.kind === "linkedin") {
    if (d.posts.some((p) => socialKind(p) === "profile")) return d.kind === "linkedin" ? "Company page" : "Full feed";
    return `${d.posts.length} ${d.posts.length === 1 ? "post" : "posts"}`;
  }
  return d.shots.length > 1 ? `${d.shots.length} pieces` : d.video ? "Film" : "1 piece";
};

/** A still at its own proportions; a tall board is held to a reading column. */
const Still = ({ shot, alt, contain, sizes }: { shot: Shot; alt: string; contain?: boolean; sizes: string }) => {
  const portrait = Boolean(shot.width && shot.height && shot.height > shot.width * 1.1);
  return (
    <figure className={cn("overflow-hidden rounded-[18px] border border-black/[0.06] bg-brand-paper", portrait && "mx-auto max-w-xl")}>
      {shot.width && shot.height ? (
        <Image
          src={shot.src}
          alt={alt}
          width={shot.width}
          height={shot.height}
          sizes={portrait ? "(min-width: 640px) 576px, 100vw" : sizes}
          className={cn("h-auto w-full", contain && "object-contain p-6")}
        />
      ) : (
        <div className="relative aspect-[16/10]">
          <Image
            src={shot.src}
            alt={alt}
            fill
            sizes={sizes}
            className={contain ? "object-contain p-6" : "object-cover"}
            style={{ objectPosition: objectPosition(shot) }}
          />
        </div>
      )}
    </figure>
  );
};

/**
 * The website, as a screenshot in a browser window, and the whole window is a link to the
 * real site in a new tab.
 *
 * Every client's site is shown the same way: a screenshot captured at laptop size (see
 * lib/clients.ts), loaded lazily, so the set reads as one consistent body of work and a
 * client view opens instantly, without waiting on someone else's site.
 */
const SiteFrame = ({ deliverable, name }: { deliverable: Deliverable; name: string }) => {
  const [lead, ...rest] = deliverable.shots;
  const host = deliverable.url ? displayHost(deliverable.url) : null;
  const Frame = deliverable.url ? "a" : "div";

  return (
    <div className="space-y-5">
      <Frame
        {...(deliverable.url ? { href: deliverable.url, target: "_blank", rel: "noreferrer noopener" } : {})}
        className="group block overflow-hidden rounded-[18px] border border-black/10 bg-white shadow-[0_30px_60px_-30px_rgba(13,27,42,0.35)] outline-none transition-shadow duration-500 hover:shadow-[0_40px_80px_-30px_rgba(13,27,42,0.45)] focus-visible:ring-2 focus-visible:ring-brand-accent"
      >
        <span className="flex items-center gap-3 border-b border-black/10 bg-brand-paper/60 px-4 py-2.5">
          <span aria-hidden className="flex gap-1.5">
            <span className="size-2.5 rounded-full bg-brand-ink/15" />
            <span className="size-2.5 rounded-full bg-brand-ink/15" />
            <span className="size-2.5 rounded-full bg-brand-ink/15" />
          </span>
          <span className="min-w-0 flex-1 truncate rounded-md bg-white px-3 py-1 text-center font-mono text-[11px] text-brand-ink/60">
            {host ?? name}
          </span>
          {deliverable.url && (
            <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-brand-accent">
              Open site
              <ArrowUpRight className="size-3 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </span>
          )}
        </span>
        <span className="relative block aspect-[16/10] overflow-hidden bg-brand-paper">
          {lead ? (
            <Image
              src={lead.src}
              alt={lead.alt ?? `${name}, the website`}
              fill
              sizes="(min-width: 1024px) 60vw, 100vw"
              className="object-cover object-top transition-transform duration-[1.2s] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.015]"
            />
          ) : (
            <span className="absolute inset-0 grid place-items-center bg-brand-navy px-8 text-center font-heading text-3xl uppercase leading-[0.9] text-white/85 sm:text-5xl">
              {host ?? name}
            </span>
          )}
        </span>
      </Frame>
      {rest.length > 0 && <Gallery shots={rest} name={name} />}
    </div>
  );
};

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Work with no screens to show, an ERP or a private platform, told as the problems it
 * solved. Set on the one dark surface the brand allows in the work section, so it reads as
 * designed rather than as a missing preview.
 */
const StoryPanel = ({ deliverable }: { deliverable: Deliverable }) => (
  <div className="overflow-hidden rounded-[22px] bg-brand-navy p-6 text-white sm:p-10">
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-mint">What it solved</p>
    <h3 className="mt-3 font-heading text-3xl uppercase leading-[0.9] tracking-tight sm:text-4xl">{deliverable.label}</h3>
    {deliverable.summary && <p className="mt-4 max-w-2xl leading-relaxed text-white/70">{deliverable.summary}</p>}
    <ol className="mt-8 divide-y divide-white/10 border-y border-white/10">
      {(deliverable.story ?? []).map((item: Story, i) => (
        <li key={item.problem} className="grid gap-5 py-6 sm:grid-cols-[2.5rem_minmax(0,1fr)] lg:grid-cols-[2.5rem_repeat(3,minmax(0,1fr))] lg:gap-8">
          <span className="font-mono text-xs tracking-[0.2em] text-brand-mint">{pad(i + 1)}</span>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">The problem</p>
            <p className="mt-2 font-semibold leading-snug">{item.problem}</p>
          </div>
          <div className="sm:col-start-2 lg:col-start-auto">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">What we built</p>
            <p className="mt-2 leading-relaxed text-white/75">{item.built}</p>
          </div>
          <div className="sm:col-start-2 lg:col-start-auto">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-mint/80">What changed</p>
            <p className="mt-2 leading-relaxed text-white/75">{item.result}</p>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

const Gallery = ({ shots, name, contain }: { shots: Shot[]; name: string; contain?: boolean }) => (
  <div className="columns-1 gap-5 sm:columns-2 [&>*]:mb-5 [&>*]:break-inside-avoid">
    {shots.map((shot, i) => (
      <Still key={`${shot.src}-${i}`} shot={shot} alt={shot.alt ?? `${name}, piece ${i + 1}`} contain={contain} sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw" />
    ))}
  </div>
);

/** One deliverable's content, drawn for what it is. */
const DeliverablePanel = ({ deliverable, name }: { deliverable: Deliverable; name: string }) => {
  switch (deliverable.kind) {
    case "website":
      return <SiteFrame deliverable={deliverable} name={name} />;
    case "erp":
      return <StoryPanel deliverable={deliverable} />;
    case "app":
      return (
        <div className="space-y-5">
          <div className="flex flex-col gap-4 rounded-[18px] border border-black/[0.08] p-6 sm:flex-row sm:items-center sm:justify-between">
            {deliverable.summary && <p className="max-w-2xl leading-relaxed text-brand-ink/75">{deliverable.summary}</p>}
            {deliverable.links && deliverable.links.length > 0 && (
              <div className="flex shrink-0 flex-wrap gap-2">
                {deliverable.links.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
                  >
                    {link.label}
                    <ArrowUpRight className="size-3.5" />
                  </a>
                ))}
              </div>
            )}
          </div>
          {deliverable.url && <SiteFrame deliverable={deliverable} name={name} />}
        </div>
      );
    case "instagram":
    case "linkedin":
      return <SocialFeed platform={deliverable.kind} posts={deliverable.posts} />;
    default: {
      const [lead, ...rest] = deliverable.shots;
      const contain = deliverable.fit === "contain";
      return (
        <div className="space-y-5">
          {deliverable.summary && (
            <p className="max-w-2xl rounded-[18px] border border-black/[0.08] p-6 leading-relaxed text-brand-ink/75">
              {deliverable.summary}
            </p>
          )}
          {deliverable.video ? (
            <video
              className="w-full rounded-[18px] border border-black/[0.06] bg-brand-paper"
              src={deliverable.video}
              poster={lead?.src}
              autoPlay
              muted
              loop
              playsInline
              aria-hidden
            />
          ) : (
            lead && <Still shot={lead} alt={lead.alt ?? `${name}, ${deliverable.label}`} contain={contain} sizes="(min-width: 1024px) 60vw, 100vw" />
          )}
          {rest.length > 0 && <Gallery shots={rest} name={name} contain={contain} />}
        </div>
      );
    }
  }
};

/**
 * The way from a client's work to a booking: the one dark panel in the aside, so it reads as
 * the next step rather than as a second button beside "visit". It starts the same booking
 * flow as every booking button on the site (BookCall), with the call and the questions named
 * on the card so a visitor knows what they are booking before it opens.
 */
const StartSomething = ({ client, className }: { client: Client; className?: string }) => (
  <BookCall
    className={cn(
      "group relative block overflow-hidden rounded-[22px] bg-brand-navy p-6 text-white outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-4 sm:p-7",
      className,
    )}
  >
    {/* A mint glow that rises on hover: opacity only, so it costs the compositor nothing. */}
    <span
      aria-hidden
      className="pointer-events-none absolute -bottom-24 -right-16 size-64 rounded-full bg-brand-mint/30 opacity-0 blur-3xl transition-opacity duration-700 group-hover:opacity-100"
    />
    <span className="relative block font-mono text-[10px] uppercase tracking-[0.22em] text-brand-mint">
      {client.services.map((s) => s.title).join(" · ")}
    </span>
    <span className="relative mt-4 block font-heading text-[1.75rem] uppercase leading-[0.92] tracking-tight sm:text-[2rem]">
      Want work like this?
    </span>
    <span className="relative mt-3 block text-sm leading-relaxed text-white/70">
      <span className="font-semibold text-white">{BOOKING_INTRO.lead}</span> {BOOKING_INTRO.body}
    </span>
    <span className="relative mt-6 flex items-center justify-between gap-4 rounded-btn bg-brand-mint px-5 py-3.5 text-sm font-semibold text-brand-ink transition-colors duration-300 group-hover:bg-white">
      Start something like this
      <span className="grid size-7 place-items-center rounded-full bg-brand-ink text-white transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-1 group-hover:-rotate-45">
        <ArrowRight className="size-3.5" />
      </span>
    </span>
    <span className="relative mt-4 block font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
      30-minute call · about 2 minutes of questions
    </span>
  </BookCall>
);

/**
 * Everything the studio did for one client.
 *
 * The left column is the client: who, which services, the line on what we did, a link to the
 * live site, and the booking card. The right is the work itself, one tab per deliverable, so a client with
 * a website, a run Instagram and a LinkedIn shows all three side by side rather than as
 * three unrelated projects. Social tabs load the platform's own embeds only once opened and
 * allowed (SocialFeed), so a client view opens instantly.
 *
 * Shared by the sheet that opens over the work section and by the /work/<slug> page.
 */
export const ClientView = ({
  client,
  number,
  headingLevel: Heading = "h2",
}: {
  client: Client;
  /** "03 / 07" */
  number?: string;
  /** h1 on the client's own page, h2 inside the sheet over the home page. */
  headingLevel?: "h1" | "h2";
}) => {
  const reduce = useReducedMotion();
  const [tab, setTab] = useState(client.deliverables[0]?.id ?? "");
  const host = client.url ? displayHost(client.url) : null;
  const lead = client.blurb ?? `${client.deliverables.map((d) => d.label).join(", ")} for ${client.name}.`;

  return (
    <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
      <aside className="lg:col-span-4">
        <div className="lg:sticky lg:top-6">
          {number && <p className="font-mono text-[11px] tracking-[0.2em] text-brand-ink/60">{number}</p>}
          <Heading className="mt-3 text-balance font-heading text-[clamp(2.25rem,3.3vw,3.5rem)] uppercase leading-[0.88] tracking-tighter text-brand-ink [overflow-wrap:anywhere]">
            {client.name}
          </Heading>
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-accent">
            {client.services.map((s) => s.title).join(" · ")}
          </p>
          <p className="mt-6 text-lg leading-snug text-brand-ink/80 sm:text-xl">{lead}</p>

          {client.url && (
            <a
              href={client.url}
              target="_blank"
              rel="noreferrer noopener"
              className="group mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-ink underline decoration-brand-mint decoration-2 underline-offset-[6px] transition-colors duration-300 hover:text-brand-accent"
            >
              Visit {host ?? "the site"}
              <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          )}

          <StartSomething client={client} className="mt-10 hidden lg:block" />
        </div>
      </aside>

      <Tabs.Root value={tab} onValueChange={setTab} className="min-w-0 lg:col-span-8">
        <Tabs.List
          aria-label={`What we did for ${client.name}`}
          className="-mx-1 flex gap-1 overflow-x-auto pb-1 [scrollbar-width:none]"
        >
          {client.deliverables.map((d) => {
            const Icon = DELIVERABLE_ICON[d.kind];
            const active = d.id === tab;
            return (
              <Tabs.Trigger
                key={d.id}
                value={d.id}
                className={cn(
                  "relative flex shrink-0 items-center gap-3 rounded-btn px-4 py-2.5 text-left outline-none transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-brand-accent",
                  active ? "text-brand-ink" : "text-brand-ink/70 hover:text-brand-ink",
                )}
              >
                {active && (
                  <motion.span
                    layoutId={`deliverable-tab-${client.slug}`}
                    transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 36 }}
                    className="absolute inset-0 rounded-btn bg-brand-mint"
                  />
                )}
                <Icon className="relative size-4" aria-hidden />
                <span className="relative">
                  <span className="block text-sm font-semibold">{d.label}</span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.16em]">{detailFor(d)}</span>
                </span>
              </Tabs.Trigger>
            );
          })}
        </Tabs.List>

        <div className="mt-6">
          <AnimatePresence mode="wait" initial={false}>
            {client.deliverables
              .filter((d) => d.id === tab)
              .map((d) => (
                <Tabs.Content key={d.id} value={d.id} forceMount asChild>
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: reduce ? 0 : 0.35, ease: EASE }}
                    className="outline-none"
                  >
                    <DeliverablePanel deliverable={d} name={client.name} />
                  </motion.div>
                </Tabs.Content>
              ))}
          </AnimatePresence>
        </div>
      </Tabs.Root>

      {/* On a phone the aside sits above the work, so the booking card waits until after it. */}
      <StartSomething client={client} className="lg:hidden" />
    </div>
  );
};
